/* eslint-disable max-statements */

import { createRef, RefObject, useMemo, useState } from "react"

export type ModalContent =
  | "deleteConfirm"
  | "deleteGuardrail"
  | "publishConfirm"
  | "sendConfirm"
  | null

export interface Props<Item = unknown> {
  fields: string[]
  isAdd?: boolean
  isEditing?: boolean
  isSaveLoading?: boolean
  item?: Item
  onCancel: () => void
  onDelete?: () => void
  onEdit?: () => void
  onPublishToggle?: () => void
  shouldCloseModalOnDeleteConfirm?: boolean
}

export interface UseItemViewReturn extends Omit<
  Props,
  "fields" | "isEditing" | "onCancel" | "onSave"
> {
  actionButtonLabel: string
  actionButtonOnClick?: () => void
  isActionButtonLoading?: boolean
  isEditMode: boolean
  isModalOpen: boolean
  modalContent: ModalContent
  onModalClose: () => void

  onModalConfirmOpen: (modalContent: ModalContent) => () => void
  onModalDeleteConfirm: () => void
  onModalPublishToggleConfirm: () => void
  outlinedButtonLabel: string
  outlinedButtonOnClick?: () => void
  refs: Record<string, RefObject<HTMLInputElement | HTMLTextAreaElement | null>>
}

const DEFAULT_MODAL_CONTENT = null

const useItemView = <Item = unknown>(props: Props<Item>): UseItemViewReturn => {
  const {
    fields,
    isAdd,
    isEditing,
    item,
    onCancel,
    onDelete,
    onEdit,
    onPublishToggle,
    shouldCloseModalOnDeleteConfirm = true
  } = props

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalContent, setModalContent] = useState<ModalContent>(null)

  const refs = fields.reduce<
    Record<string, RefObject<HTMLInputElement | HTMLTextAreaElement | null>>
  >((acc, field) => {
    acc[field] = createRef<HTMLInputElement | HTMLTextAreaElement>()
    return acc
  }, {})

  const isEditMode = Boolean(isAdd || isEditing)

  const actionButtonLabel = isEditMode ? "Save" : "Delete"
  const outlinedButtonLabel = isEditMode ? "Cancel" : "Edit"

  const isActionButtonLoading = useMemo(
    () => (isEditMode && props.isSaveLoading) || false,
    [isEditMode, props.isSaveLoading]
  )

  const onModalConfirmOpen = (newModalContent: ModalContent) => () => {
    setModalContent(newModalContent)
    setIsModalOpen(true)
  }

  const actionButtonOnClick = isEditMode
    ? undefined
    : onModalConfirmOpen("deleteConfirm")

  const outlinedButtonOnClick = isEditMode ? onCancel : onEdit

  const onModalClose = () => {
    setModalContent(DEFAULT_MODAL_CONTENT)
    setIsModalOpen(false)
  }

  const onModalDeleteConfirm = () => {
    onDelete?.()
    if (shouldCloseModalOnDeleteConfirm) {
      onModalClose()
    }
  }

  const onModalPublishToggleConfirm = () => {
    onPublishToggle?.()
    onModalClose()
  }

  return {
    actionButtonLabel,
    actionButtonOnClick,
    isActionButtonLoading,
    isAdd,
    isEditMode,
    isModalOpen,
    item,
    modalContent,
    onModalClose,
    onModalConfirmOpen,
    onModalDeleteConfirm,
    onModalPublishToggleConfirm,
    outlinedButtonLabel,
    outlinedButtonOnClick,
    refs
  }
}

export default useItemView
